import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext } from '../../context/auth/auth-context'
import useSWR from 'swr'
import axios from 'axios'
import { axiosFetcher } from '../../utils/api/axios-fetcher'
import { Servers, formStrategyApiUrl } from '../../utils/api/servers'
import { useScheduledSnapshot } from '../../hooks/strategy/use-scheduled-snapshot'
import { useMainSummaryDataGridWorker } from '../../hooks/strategy/use-main-summary-data-grid-worker'
import { publicIpv4 } from 'public-ip'
import { useForm } from 'react-hook-form'
import { type AlertColor, Chip, IconButton, Stack, Tooltip, Typography, Grid, Divider } from '@mui/material'
import PowerOffIcon from '@mui/icons-material/PowerOff'
import SaveIcon from '@mui/icons-material/Save'
import RefreshIcon from '@mui/icons-material/Refresh'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { type IStrategyInfo } from '../../models/strategy/strategy-info-types'
import { type IStrategyComponentRecord } from '../../models/strategy/strategy-component-types'
import StrategyComponentCard, { type StrategyComponentFormInputs } from '../../components/card/strategy/strategy-component-card'
import StrategyComponentAccountSummary from './strategy-component-account-summary'
import { isEmptyString } from '../../utils/string-utils'
import { formatToLocalMilliTimestampFromUTCString, } from '../../utils/time-utils'
import {
  EXCLUDED_FROM_DISPLAY_COMPONENTS,
  preparePOSTStrategyParameters,
  processComponentData,
  processStrategyMetadata,
} from '../../utils/view-specific/strategy/strategy-utils'
import { DISPLAY_TYPE } from '../_composite/strategy-trade-book-composite'
import Loader from '../../components/loader/loader'
import RefreshFeesTableButton from './button/refresh-fees-table-button'
import SaveSnapshotButton from './button/save-snapshot-button'
import RiskModalTrigger from './modal/risk/risk-modal-trigger'
import RiskLoaderModalTrigger from './modal/loader/risk-loader-modal-trigger'
import OrderManagerModalTrigger from './modal/order-manager/order-manager-modal-trigger'
import AccountsModalTrigger from './modal/accounts/accounts-modal-trigger'
import GatewayModalTrigger from './modal/gateway/gateway-modal-trigger'
import ConstraintsModalTrigger from './modal/constraints/constraints-modal-trigger'
import LiveOrdersModalTrigger from './modal/live-orders/live-orders-modal-trigger'
import TradingStatusChip from './trading-status-chip'
import ResumeTradingButton from './button/resume-trading-button'
import PauseTradingButton from './button/pause-trading-button'
import StrategyKillModalTrigger from './modal/strategy-kill/strategy-kill-modal-trigger'
import StrategyComponentMainSummary from './strategy-component-main-summary'
import ToastWithMessage from '../../components/toast/toast-with-message'

interface StrategyComponentProps {
  strategyId: string
  strategyName: string
  displayType: DISPLAY_TYPE
}

const StrategyComponent: React.FC<StrategyComponentProps> = ({ strategyId, strategyName, displayType }) => {
  // Connection States
  const [attemptedApiConnect, setAttemptedApiConnect] = useState<boolean>(false)
  const [apiConnected, setApiConnected] = useState<boolean>(false)
  const [loadedDataAtLeastOnce, setLoadedDataAtLeastOnce] = useState<boolean>(false)

  // Data URLs
  const [riskUrl, setRiskUrl] = useState<string>('')
  const [riskLoaderDataUrl, setRiskLoaderDataUrl] = useState<string>('')
  const [accountsUrl, setAccountsUrl] = useState<string>('')
  const [constraintsUrl, setConstraintsUrl] = useState<string>('')
  const [liveOrdersUrl, setLiveOrdersUrl] = useState<string>('')
  const [orderManagerInfoUrl, setOrderManagerInfoUrl] = useState<string>('')
  const [gatewayInfoUrl, setGatewayInfoUrl] = useState<string>('')

  // Control URLs
  const [updateParametersUrl, setUpdateParametersUrl] = useState<string>('')
  const [executeCommandUrl, setExecuteCommandUrl] = useState<string>('')
  const [updateConstraintUrl, setUpdateConstraintUrl] = useState<string>('')
  const [orderManagerCommandUrl, setOrderManagerCommandUrl] = useState<string>('')
  const [gatewayCommandUrl, setGatewayCommandUrl] = useState<string>('')
  const [closeOrderUrl, setCloseOrderUrl] = useState<string>('')
  const [killUrl, setKillUrl] = useState<string>('')

  useEffect(() => {
    const invalidStrategyId = isEmptyString(strategyId)
    const isDevNodeEnv = process.env.NODE_ENV === 'development'
    const isDevReactEnv = process.env.REACT_APP_ENVIRONMENT === 'development'
    const useLocalStrategy = (isDevNodeEnv && isDevReactEnv)

    if (invalidStrategyId && !useLocalStrategy) {
      // Data URLs
      setRiskUrl('')
      setRiskLoaderDataUrl('')
      setAccountsUrl('')
      setConstraintsUrl('')
      setOrderManagerInfoUrl('')
      setGatewayInfoUrl('')
      setLiveOrdersUrl('')

      // Control URLs
      setUpdateParametersUrl('')
      setExecuteCommandUrl('')
      setUpdateConstraintUrl('')
      setOrderManagerCommandUrl('')
      setGatewayCommandUrl('')
      setCloseOrderUrl('')
      setKillUrl('')
      return
    }

    // Data URLs
    setRiskUrl(formStrategyApiUrl(strategyId, 'strategy-management/risk'))
    setRiskLoaderDataUrl(formStrategyApiUrl(strategyId, 'strategy-management/strategyInitialRisk'))
    setAccountsUrl(formStrategyApiUrl(strategyId, 'strategy-management/accounts'))
    setConstraintsUrl(formStrategyApiUrl(strategyId, 'oms-management/constraints'))
    setOrderManagerInfoUrl(formStrategyApiUrl(strategyId, 'gateway-management/orderManagerInfo'))
    setGatewayInfoUrl(formStrategyApiUrl(strategyId, 'gateway-management/gatewayInfo'))
    setLiveOrdersUrl(formStrategyApiUrl(strategyId, 'oms-management/liveOrders'))

    // Control URLs
    setUpdateParametersUrl(formStrategyApiUrl(strategyId, 'strategy-management/updateParameters'))
    setExecuteCommandUrl(formStrategyApiUrl(strategyId, 'strategy-management/executeCommand'))
    setUpdateConstraintUrl(formStrategyApiUrl(strategyId, 'oms-management/updateConstraint'))
    setOrderManagerCommandUrl(formStrategyApiUrl(strategyId, 'gateway-management/orderManagerCommand'))
    setGatewayCommandUrl(formStrategyApiUrl(strategyId, 'gateway-management/gatewayCommand'))
    setCloseOrderUrl(formStrategyApiUrl(strategyId, 'oms-management/closeOrder'))
    setKillUrl(formStrategyApiUrl(strategyId, 'strategy-management/kill'))
  }, [strategyId])

  // Strategy Metadata
  const [updateTimestamp, setUpdateTimestamp] = useState<string>('')
  const [updateType, setUpdateType] = useState<string>('')

  useEffect(() => {
    // Clear cached state when strategyId changes
    setAttemptedApiConnect(false)
    setApiConnected(false)
    setLoadedDataAtLeastOnce(false)

    setUpdateTimestamp('')
    setUpdateType('')

    setComponentMap({})
  }, [strategyId])

  const { isAuthorized, accessToken } = useContext(AuthContext)
  const { data, error, mutate } = useSWR<IStrategyInfo[], boolean>(
    isAuthorized
      ? [
          Servers.STRATEGY_BASE,
          strategyId,
          (displayType === DISPLAY_TYPE.DETAILED || displayType === DISPLAY_TYPE.DETAILED_NO_TRADES)
            ? 'strategy-management/strategyInfo'
            : 'strategy-management/strategyInfoSimple',
          accessToken,
          null,
        ]
      : null,
    axiosFetcher,
    {
      shouldRetryOnError: true,
      refreshInterval: 5000,
      onSuccess: () => {
        setAttemptedApiConnect(true)
      },
      onError: () => {
        setAttemptedApiConnect(true)
      },
    },
  )

  // Scheduled Snapshot Saving
  const strategyDataRef = useScheduledSnapshot(strategyId)

  const [componentMap, setComponentMap] = useState<IStrategyComponentRecord>({})
  useEffect(() => {
    if (error === true || data == null || data.length === 0) {
      setApiConnected(false)
      setUpdateType('')
    } else {
      setApiConnected(true)
      setLoadedDataAtLeastOnce(true)

      const metadata = processStrategyMetadata(data[0])
      setUpdateTimestamp(metadata.timestamp)
      setUpdateType(metadata.updateType)

      const dataMap = processComponentData(data[0])
      setComponentMap(dataMap)
      strategyDataRef.current = dataMap // Update ref for export purposes
    }
  }, [data, error])

  const reloadForm = useCallback((withFeedback = false): void => {
    void (async () => {
      if (withFeedback) {
        mutate() // Pull latest data then invoke toast
          .then(data => {
            if (data === undefined) {
              displayToastWithMessage('Error encountered during refresh', 'error')
            } else {
              displayToastWithMessage('Refresh complete', 'success')
            }
          })
          .catch(() => {
            displayToastWithMessage('Error encountered during refresh', 'error')
          })
      } else {
        await mutate() // Pull latest data only
      }
      reset() // Reset form cache
    })()
  }, [strategyId])

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState<AlertColor>('info')
  const displayToastWithMessage = useCallback((message: string, severity: AlertColor): void => {
    setToastMessage(message)
    setToastSeverity(severity)
    setToastOpen(true)
  }, [strategyId])

  const [componentForm, setComponentForm] = useState<Record<string, Record<string, string | number | boolean>>>({})

  const {
    handleSubmit,
    reset,
    control,
    formState,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { dirtyFields }, // Read the formState before render to subscribe to all changes through Proxy
  } =
    useForm<StrategyComponentFormInputs>({
      defaultValues: componentForm,
    })

  const handleStrategyUpdate = useCallback((data: StrategyComponentFormInputs): void => {
    if (updateParametersUrl == null || updateParametersUrl.length === 0) {
      console.error('[Strategy] Failed to update parameters (reason: invalid update url)')
      return
    }

    publicIpv4()
      .then(
        ipAddress => {
          console.debug(`[Strategy] Updating strategy: ${strategyName}`)
          console.debug('[Strategy] Snapshot of update (before processing):')
          console.debug(data)

          const processedUpdateBody = preparePOSTStrategyParameters(
            formState.dirtyFields,
            strategyId,
            'user',
            ipAddress,
            data,
          )

          console.debug('[Strategy] Snapshot of update (after processing):')
          console.debug(processedUpdateBody)
          return processedUpdateBody
        },
      )
      .then(async body => {
        await axios.post(updateParametersUrl, body, { headers: { Authorization: `Bearer ${accessToken}` } })
      })
      .then(response => {
        console.debug(response)
        displayToastWithMessage(
          'Strategy update succeeded',
          'success',
        )
      })
      .catch(reason => {
        console.error(reason)
        displayToastWithMessage(
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          `Strategy update failed (${reason?.message})
          ( Full error can be viewed in the console logs )`,
          'error',
        )
      })
      .finally(() => {
        reloadForm(false)
      })
  }, [strategyId, strategyName, updateParametersUrl, formState, accessToken])

  const formWorker: Worker = useMemo(
    () => new Worker(new URL('../../workers/strategy/form-worker.ts', import.meta.url)),
    [],
  )
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (window.Worker) {
      formWorker.postMessage(componentMap)
    }
  }, [formWorker, componentMap])
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (window.Worker) {
      formWorker.onmessage = (e: MessageEvent<Record<string, Record<string, string | number | boolean>>>) => {
        const componentForm = e.data
        setComponentForm(componentForm)
        // defaultValues are cached on the first render, so we must reset on state change
        reset(componentForm, { keepDirty: true, keepDirtyValues: true })
      }
    }
  }, [formWorker])

  const { dataGridRows, mainSummaryData } = useMainSummaryDataGridWorker(componentMap)

  if (!apiConnected && !loadedDataAtLeastOnce) {
    return (
      <Stack
        width='100vw' height='100%' padding={1} gap={1}
        border='1px solid rgba(255, 255, 255, 0.12)'
        alignItems='center' justifyContent='center'
      >
        {
          attemptedApiConnect
            ? <PowerOffIcon color='error' fontSize='large'/>
            : <Loader/>
        }
        <Typography color='primary' align='center'>
          {
            attemptedApiConnect
              ? 'Unable to connect to selected strategy'
              : 'Connecting...'
          }
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack width={1} padding={1} border='1px solid rgba(255, 255, 255, 0.12)'>
      <Grid container spacing={1} alignItems='center' justifyContent='flex-end'>
        <Grid item>
          <Typography color='primary' variant='h6' fontWeight='bold'>
            {strategyName}
          </Typography>
        </Grid>

        <Grid item>
          {
            apiConnected
              ? <Chip
                label={updateType}
                variant='outlined'
                size='small'
              />
              : <Chip
                icon={<ErrorOutlineIcon/>}
                label='DISCONNECTED'
                color='error'
                size='small'
              />
          }
        </Grid>

        <Grid item xs md='auto'>
          <Chip
            label={formatToLocalMilliTimestampFromUTCString(updateTimestamp)}
            size='small'
          />
        </Grid>

        <Grid item>
          <Tooltip title='Reload'>
            <IconButton
              size='small'
              onClick={() => {
                reloadForm(true)
              }}
            >
              <RefreshIcon/>
            </IconButton>
          </Tooltip>
        </Grid>

        <Grid item alignSelf='stretch'>
          <Divider orientation='vertical'/>
        </Grid>

        <Grid item>
          <RefreshFeesTableButton
            strategyId={strategyId}
            isApiConnected={apiConnected}
            controlExecuteCommandUrl={executeCommandUrl}
            displayToastWithMessage={displayToastWithMessage}
          />
        </Grid>

        <Grid item>
          <SaveSnapshotButton
            strategyId={strategyId}
            strategyDataRef={strategyDataRef}
            displayToastWithMessage={displayToastWithMessage}
          />
        </Grid>

        <Grid item alignSelf='stretch'>
          <Divider orientation='vertical'/>
        </Grid>

        <Grid item>
          <RiskModalTrigger
            strategyName={strategyName}
            isApiConnected={apiConnected}
            riskUrl={riskUrl}
          />
        </Grid>

        <Grid item>
          <RiskLoaderModalTrigger
            strategyId={strategyId}
            strategyName={strategyName}
            isApiConnected={apiConnected}
            riskLoaderDataUrl={riskLoaderDataUrl}
            riskLoaderPostUrl={executeCommandUrl}
          />
        </Grid>

        <Grid item alignSelf='stretch'>
          <Divider orientation='vertical'/>
        </Grid>

        <Grid item>
          <OrderManagerModalTrigger
            strategyId={strategyId}
            strategyName={strategyName}
            isApiConnected={apiConnected}
            orderManagerInfoUrl={orderManagerInfoUrl}
            controlOrderManagerCommandUrl={orderManagerCommandUrl}
          />
        </Grid>

        <Grid item>
          <AccountsModalTrigger
            strategyId={strategyId}
            strategyName={strategyName}
            isApiConnected={apiConnected}
            accountsUrl={accountsUrl}
            controlExecuteCommandUrl={executeCommandUrl}
          />
        </Grid>

        <Grid item>
          <GatewayModalTrigger
            strategyId={strategyId}
            strategyName={strategyName}
            isApiConnected={apiConnected}
            gatewayInfoUrl={gatewayInfoUrl}
            gatewayCommandUrl={gatewayCommandUrl}
          />
        </Grid>

        <Grid item alignSelf='stretch'>
          <Divider orientation='vertical'/>
        </Grid>

        <Grid item>
          <ConstraintsModalTrigger
            strategyId={strategyId}
            strategyName={strategyName}
            isApiConnected={apiConnected}
            constraintsUrl={constraintsUrl}
            controlUpdateConstraintUrl={updateConstraintUrl}
          />
        </Grid>

        <Grid item>
          <LiveOrdersModalTrigger
            strategyId={strategyId}
            strategyName={strategyName}
            isApiConnected={apiConnected}
            liveOrdersUrl={liveOrdersUrl}
            controlCloseOrderUrl={closeOrderUrl}
          />
        </Grid>

        <Grid item alignSelf='stretch'>
          <Divider orientation='vertical'/>
        </Grid>

        <Grid item>
          <Tooltip title='Update parameters'>
              <span>
                <IconButton
                  size='small'
                  form={`${strategyId}-form`}
                  type='submit'
                  disabled={isEmptyString(updateParametersUrl) || !apiConnected}
                >
                  <SaveIcon/>
                </IconButton>
              </span>
          </Tooltip>
        </Grid>

        <Grid item xs/>

        <Grid item>
          <Stack direction='row' gap={2} marginX={1} alignItems='center'>
            <TradingStatusChip strategyId={strategyId}/>
            <ResumeTradingButton
              strategyId={strategyId}
              strategyName={strategyName}
              isApiConnected={apiConnected}
              controlExecuteCommandUrl={executeCommandUrl}
              displayToastWithMessage={displayToastWithMessage}
            />
            <PauseTradingButton
              strategyId={strategyId}
              strategyName={strategyName}
              isApiConnected={apiConnected}
              controlExecuteCommandUrl={executeCommandUrl}
              displayToastWithMessage={displayToastWithMessage}
            />
          </Stack>
        </Grid>

        <Grid item alignSelf='stretch'>
          <Divider orientation='vertical'/>
        </Grid>

        <Grid item>
          <StrategyKillModalTrigger
            strategyName={strategyName}
            isApiConnected={apiConnected}
            controlKillUrl={killUrl}
          />
        </Grid>
      </Grid>

      <StrategyComponentMainSummary
        dataGridRows={dataGridRows}
        mainSummaryData={mainSummaryData}
        csvBaseFileName={strategyName}
      />

      <form
        id={`${strategyId}-form`}
        /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
        onSubmit={handleSubmit(handleStrategyUpdate)}
        noValidate
        style={{ opacity: (!apiConnected ? 0.6 : 1.0) }}
      >
        <fieldset disabled={!apiConnected} style={{ display: 'contents', width: '100%' }}>
          {
            Object.entries(componentMap)
              .map(([name, component]) => {
                let displayStates = true
                let displayParameters = true

                const exactMatch = EXCLUDED_FROM_DISPLAY_COMPONENTS.includes(name)
                if (exactMatch) { // Match most specific names first
                  displayStates = false
                }

                if (displayStates) {
                  for (const componentName of EXCLUDED_FROM_DISPLAY_COMPONENTS) {
                    if (name.includes(componentName)) {
                      displayStates = false
                      break
                    }
                  }
                }

                if (Object.keys(component.data.parameters).length === 0) {
                  displayParameters = false
                }

                if (!displayStates && !displayParameters) {
                  return null
                } else {
                  return (
                    <StrategyComponentCard
                      key={name}
                      strategy={component}
                      states={displayStates ? component.data.states : {}}
                      parameters={component.data.parameters}
                      control={control}
                    />
                  )
                }
              })
          }
        </fieldset>
      </form>

      {
        (displayType === DISPLAY_TYPE.SIMPLE || displayType === DISPLAY_TYPE.SIMPLE_NO_TRADES)
          ? null
          : <StrategyComponentAccountSummary rows={dataGridRows}/>
      }

      <ToastWithMessage toastOpen={toastOpen} setToastOpen={setToastOpen} message={toastMessage} severity={toastSeverity}/>
    </Stack>
  )
}

export default StrategyComponent
